/********************************************* ASSETS *********************************************/
const defaultBannerImg = require('../../assets/img/account_settings_top_banner_sm.png');

/******************************************** STYLING *********************************************/
const s = require('./content-navbar.scss');

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import {inject, observer} from 'mobx-react';
import {observable, action} from 'mobx';
import {get, IfTruthy} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {
    translate,
    StoreProps,
    ConfigProps,
    onSubmitKeyPress,
    onArrowPress,
    SolidDot,
    CILink,
} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
interface TabOptions {
    id: string;
    route?: string;
    icon?: {active: string; inactive: string};
}

interface ContentNavbarSharedProps {
    tabList: TabOptions[];
    activeTab: string;
    onClick: (id: string, route?: string, elementID?: string) => any;
    tabAriaTemplate: string;
}

interface ContentNavbarProps extends ContentNavbarSharedProps, StoreProps, ChildrenPassthruProps {
    title: string;
    status?: string;
    showSettings?: boolean;
    showRecordLink?: boolean;
    scrollTop: number;
}

/******************************************** HELPERS *********************************************/
/**
 * Icon + text link beside the content header title (record's "name")
 *
 * [children] is the text to display (on wider screens)
 * [iconClass] is used the render the icon itself
 * [label] provides the tooltip text & part of the aria-label
 * [recordFullName] contains the name of the person linked to the record
 * Links to [url]
 * Accepts [id] HTML attribute
 *
 * Example output:
 *
 *  ⚙  Settings
 */
const ContentHeaderLink = inject('config')(
    (
        props: {
            id: string;
            recordFullName: string;
            label: string;
            url: string;
            iconClass: string;
            children: string;
        } & ConfigProps
    ) => (
        <CILink
            id={props.id}
            className={cn(
                props.config.elementIds.mainContentStart,
                s[`header-title-row-link`],
                `ml-25`
            )}
            aria-label={`${props.recordFullName} - ${props.label}`}
            to={props.url}
        >
            <div className={cn(`flex row ai-center`)} title={props.label}>
                <div className={cn(props.iconClass)}>{props.children}</div>
            </div>
        </CILink>
    )
);

ContentHeaderLink.displayName = `ContentHeaderLink`;

/**
 * Header/description area above tab navbar
 *
 * Optionally has children to show above tabs, settings link, title, & status dot
 *
 * If [status] given, displays a dot coloured current record's status
 * Shows [title] above passed-in children (& beside status dot if status given)
 *
 * [children] = all content between the title row and the tabs
 *
 * Example outputs
 * ===============
 * 1. No children:
 *
 *  ○  Meeka Faulkner    ⚙  Settings
 *
 * 2. Info text & badges as children:
 *
 *  ○  Meeka Faulkner    ⚙  Settings
 *     Born Oct 31, 2017  •  Male  • Ontario Immunization Schedule
 *
 *      FLU SHOT    OVERDUE
 *
 *     Last edited: Last Friday, 2:11AM
 *
 */
const ContentHeaderDescription = inject('config')(
    (
        props: {
            recordId: string;
            title: string;
            status?: string;
            showSettings?: boolean;
            showRecordLink?: boolean;
            children?: ReactChildren;
        } & ConfigProps
    ) => (
        <section className={cn(`flex col space-between`, `mt-25 mr-25 mb-15 ml-50`)}>
            <div className={cn(s[`name-header`], `relative`, `mb-5`, `helvetica-neue`)}>
                {props.status && (
                    <SolidDot
                        diameterInPx={8}
                        color={props.status}
                        className={cn(`absolute`, `mt-12 ml-n25`)}
                    />
                )}
                <div className={cn(`flex row`, `mb-2`)}>
                    {/* TODO CONVERT TO h2 or h3 */}
                    <div>{props.title}</div>

                    {/* RECORD LINK */}
                    <IfTruthy test={props.showRecordLink}>
                        <ContentHeaderLink
                            id={props.config.elementIds.main.recordSettings.headerRecordLink}
                            recordFullName={props.title}
                            label={translate('title_records')}
                            url={props.config.routes.myRecords(props.recordId)}
                            iconClass={s[`record-icon`]}
                        >
                            {translate(`back_to_record`)}
                        </ContentHeaderLink>
                    </IfTruthy>

                    {/* SETTINGS LINK */}
                    <IfTruthy test={props.showSettings}>
                        <ContentHeaderLink
                            id={props.config.elementIds.main.myRecords.headerSettingsLink}
                            recordFullName={props.title}
                            label={translate('record_settings')}
                            url={props.config.routes.settings.record(props.recordId)}
                            iconClass={s[`cog`]}
                        >
                            {translate(`settings`)}
                        </ContentHeaderLink>
                    </IfTruthy>
                </div>
                {props.children}
            </div>
        </section>
    )
);

ContentHeaderDescription.displayName = `ContentHeaderDescription`;

/**
 * Build ID to display on tab DOM element
 */
const buildTabElId = (id: string) => `navbar_tab_${id.snakeCase()}`;

/**
 * TODO refactor onKeyUp - extract to outside fn, or hoist to container (TabWrapper)
 *
 * Actual tab navigation bar, with tab "slider", and all tabs
 *
 * Tabs are configured by [tabList]
 * Currently active tab selected by [activeTab]
 * On tab click, run [onClick] function, passing in the ID of the selected tab
 */
const TabWrapper = (props: ContentNavbarSharedProps & {hover: string; onHover: any}) => {
    const {tabList, onClick, onHover, tabAriaTemplate, hover} = props;

    // Set default active tab if tab not found
    let activeTab = props.activeTab;
    if (!tabList.find(tabObj => tabObj.id === activeTab)) {
        activeTab = tabList[0].id;
    }

    // Calculate widths for shifting underline identifying current tab
    const activeWidth = 100 / tabList.length;
    const activeOffset = activeWidth * tabList.findIndex(o => o.id === activeTab);
    const currentTabIdx = tabList.findIndex(tab => tab.id === activeTab);
    const tabListLen = tabList.length;

    return (
        <div className={cn(s[`tabWrapper`])}>
            {tabList.map(({id, route, icon}, idx) => {
                const isActive = id === activeTab;
                const isHover = id === hover && !isActive;
                const tabTitle = translate(id === `childhood` ? `vaccinations_child` : id);
                const ariaLabel = translate(tabAriaTemplate, {
                    '%1$s': tabTitle,
                });

                const iconComponent = icon && (
                    <img
                        className={cn(s[`tab-icon`], isHover ? `opa-80` : `opa-100`)}
                        src={isActive || isHover ? icon.active : icon.inactive}
                    />
                );

                return (
                    <div
                        role={`tab`}
                        tabIndex={0}
                        aria-label={ariaLabel}
                        onMouseOut={onHover(``)}
                        onMouseOver={onHover(id)}
                        onClick={onClick(id, route)}
                        onKeyUp={event => {
                            onSubmitKeyPress(onClick(id, route, buildTabElId(id)))(event);
                            onArrowPress({
                                left: ev => {
                                    const newId =
                                        idx === 0
                                            ? tabList[tabListLen - 1].id
                                            : tabList[idx - 1].id;
                                    document.getElementById(buildTabElId(newId)).click();
                                    setTimeout(
                                        () => document.getElementById(buildTabElId(newId)).focus(),
                                        10
                                    );
                                    return onClick(newId, route, buildTabElId(newId))(ev);
                                },
                                right: ev => {
                                    const newId =
                                        idx === tabListLen - 1
                                            ? tabList[0].id
                                            : tabList[idx + 1].id;
                                    document.getElementById(buildTabElId(newId)).click();
                                    setTimeout(
                                        () => document.getElementById(buildTabElId(newId)).focus(),
                                        10
                                    );
                                    return onClick(newId, route, buildTabElId(newId))(ev);
                                },
                            })(event);
                        }}
                        key={buildTabElId(id)}
                        id={buildTabElId(id)}
                        style={{width: `${activeWidth}%`}}
                        className={cn(
                            s[`item`],
                            isActive ? s[`active`] : s[`inactive`],
                            isHover && !isActive ? s[`hover`] : null
                        )}
                    >
                        {iconComponent}
                        {tabTitle}
                    </div>
                );
            })}
            <div className={cn(s[`selector`])}>
                <div
                    style={{
                        width: `${activeWidth}%`,
                        marginLeft: `${activeOffset}%`,
                    }}
                />
            </div>
        </div>
    );
};

TabWrapper.displayName = `TabWrapper`;

/********************************************* EXPORT *********************************************/
/**
 * Content area with:
 * - "heading" section at the top
 * - Banner image to right of header area at the top
 * - Tabbed nav bar comprised of a tab "slider" and selectable tabs
 *
 * Clicking a tab selects it, and runs a function intended to display content
 * in the area bbelow the navbar
 *
 * Note that this don't contain the actual content area below the tabs
 */
@observer
export class ContentNavbar extends React.Component<ContentNavbarProps> {
    contentElement = React.createRef<HTMLDivElement>();
    tabElement = React.createRef<HTMLDivElement>();

    /********** LOCAL MOBX STORE **********/
    /**
     * Height of content area below tab section
     */
    @observable
    contentHeight = 0;

    /**
     * Height of tab section
     */
    @observable
    tabHeight = 0;

    /**
     * Currently hovered tab
     */
    @observable
    hover = ``;
    /**************************************/

    componentDidMount() {
        this.updateElementHeights();
    }

    public get isSticky(): boolean {
        return this.props.scrollTop > this.contentHeight;
    }

    /**
     * Set currently hovered tab
     */
    public updateHover = (tab: string) => action(() => (this.hover = tab));

    /**
     * Allow content height to update based on current tab & element height
     *
     * These values are used elsewhere to shrink content navbar at the top of
     * the content section as the user scrolls down the content area
     */
    private updateElementHeights = action(() => {
        this.contentHeight = this.contentElement.current.clientHeight;
        this.tabHeight = this.tabElement.current.clientHeight;
    });

    render() {
        const {children, ...propsNoChildren} = this.props;

        const barHeight = this.contentHeight + this.tabHeight;
        let currentTab = this.props.tabList.find(t => t.id === this.props.activeTab);
        currentTab = currentTab || this.props.tabList[0];

        const opacityStyle = {
            opacity: 1 - this.props.scrollTop / this.contentHeight || 0,
        };
        const displayStyle = this.isSticky ? cn(`h-0`, `m-0 p-0`, `vis-hidden`) : null;
        const spacerStyle = {marginTop: barHeight};

        const recordId = get(this, `props.appState.activeRecordModel.recordId`, ``);

        return (
            <nav>
                {this.isSticky && <div style={spacerStyle} />}
                <div className={cn(s[`wrapper`], cn({[s[`sticky`]]: this.isSticky}))}>
                    <div className={cn(s[`content`])}>
                        {/******* HEADING ABOVE TAB NAVBAR *******/}
                        {/* [Note: below renders `children` prop] */}
                        <div ref={this.contentElement} className={displayStyle}>
                            <ContentHeaderDescription recordId={recordId} {...propsNoChildren}>
                                {children}
                            </ContentHeaderDescription>
                        </div>

                        {/******** TAB NAVBAR ********/}
                        <div ref={this.tabElement}>
                            <TabWrapper
                                hover={this.hover}
                                onHover={this.updateHover}
                                tabList={this.props.tabList}
                                activeTab={this.props.activeTab}
                                onClick={this.props.onClick}
                                tabAriaTemplate={this.props.tabAriaTemplate}
                            />
                        </div>
                    </div>

                    {/******** BANNER (above navbar, right of heading) ********/}
                    <img
                        src={get(currentTab, `img`) || defaultBannerImg}
                        className={cn(s[`banner`], displayStyle)}
                        style={opacityStyle}
                    />
                </div>
            </nav>
        );
    }
}
